import { QuestionCircleOutlined } from '@ant-design/icons'
import { Button, Col, Form, InputNumber, message, Row, Tag, Tooltip } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import { TokenizeStatusEnum } from 'constants/pto'
import { SELLING_STATUS_CONFIG, SellingStatusEnum } from 'constants/pto-v2'
import { usePTOV2Context } from 'context/pto-v2-context-provider'
import {
  cleanPhasesV2,
  initNftRarities,
  initTimeLinesCreate,
  initTimeLinesEdit,
} from 'extends/pto-v2.extend'
import { customErrorContract } from 'hooks/contract/use-contract-viem'
import { useCreatePool } from 'hooks/pto-v2/use-create-pool'
import { usePoolInfo } from 'hooks/pto-v2/use-pool-info'
import ButtonUploadWhitelist from 'modules/pto-v2/form/button-upload-whitelist'
import Timelines from 'modules/pto-v2/form/timelines'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { notifyFormErrors } from 'utils/form-errors'
import { logError, logInfo } from 'utils/logger'

/**
 * PTO v2 - Selling Details tab.
 * Off Sale: show "List For Sale" -> enable PTO Price, Transaction Fee,
 * Timelines, Whitelist -> createPool on PTODistribution + update BE.
 * On Sale/Sold Out: price/fee/timelines are read-only, whitelist stays open.
 */
const SellingDetails = () => {
  const [form] = Form.useForm()
  const [listing, setListing] = useState(false)

  const { address } = useAccount()
  const { code, ptoDetail, contractAddress, getPTODetail } = usePTOV2Context()
  const { isContractManager, createPool } = useCreatePool()
  const { poolInfo, getPoolInfo } = usePoolInfo(contractAddress)

  const sellingStatus: SellingStatusEnum = ptoDetail?.sellingStatus ?? SellingStatusEnum.OFF_SALE
  const sellingStatusConfig =
    SELLING_STATUS_CONFIG[sellingStatus] || SELLING_STATUS_CONFIG[SellingStatusEnum.OFF_SALE]

  // A pool may already exist on-chain while the API status has not been
  // updated yet - also check getPoolIds on the contract before allowing edit
  const hasPoolOnChain = !!poolInfo?.poolIds?.length
  const isOffSale = sellingStatus === SellingStatusEnum.OFF_SALE && !hasPoolOnChain
  const editable = isOffSale && listing
  // Whitelist can be uploaded while listing and stays enabled after On Sale
  const whitelistEdit = editable || !isOffSale

  const initForm = useCallback(() => {
    if (!ptoDetail) return
    form.setFieldsValue({
      maxNFTSupply: ptoDetail?.maxNFTSupply,
      pricePerNFT: ptoDetail?.pricePerNFT,
      transactionFee: ptoDetail?.buyingFeeBP,
      phases: ptoDetail?.phases?.length
        ? initTimeLinesEdit(ptoDetail.phases)
        : initTimeLinesCreate(),
    })
  }, [form, ptoDetail])

  useEffect(() => {
    if (!listing) {
      initForm()
    }
  }, [initForm, listing])

  const handleStartListing = () => {
    setListing(true)
  }

  const handleCancel = () => {
    setListing(false)
    initForm()
  }

  console.log('isContractManager', isContractManager)
  const handleListForSale = async () => {
    try {
      if (!address) {
        message.error('Please connect wallet!')
        return
      }
      if (!isContractManager) {
        message.error('Your wallet address does not have permission to list PTO for sale!')
        return
      }
      if (!contractAddress) {
        message.error('PTO collection contract has not been deployed yet!')
        return
      }

      const values = await form.validateFields()

      const phases = cleanPhasesV2(values.phases)
      if (!phases.length) {
        message.error('Please setup timelines!')
        return
      }
      const nftRarities = initNftRarities(ptoDetail?.goldenTickets)

      onOpenModalProcessing()

      const poolArgs = {
        pricePerNFT: values.pricePerNFT,
        tradeUnlockTime: ptoDetail?.tradeUnlockTime || 0,
        ptoAddress: contractAddress,
        buyingFeeBP: values.transactionFee,
        phases,
        nftRarities,
      }
      logInfo('Creating pool with args:', poolArgs)

      const poolId = await createPool(address, poolArgs)
      logInfo('Pool created, poolId:', poolId)

      // Append the new poolId to the PTO document (PATCH /admin/pto/:id/contract)
      if (poolId !== null && poolId !== undefined) {
        await AppAdmin.ptoUpdateContract(code as string, {
          status: ptoDetail?.status ?? TokenizeStatusEnum.ACTIVE,
          contractAddress,
          poolId,
        })
      }

      await AppAdmin.ptoV2UpdateSellingDetails(code as string, {
        pricePerNFT: values.pricePerNFT,
        buyingFeeBP: values.transactionFee,
        phases,
        sellingStatus: SellingStatusEnum.ON_SALE,
      })

      message.success('PTO listed for sale successfully!')
      setListing(false)
      getPTODetail()
      getPoolInfo()
    } catch (error) {
      const errorMessage = customErrorContract(error)
      message.error(errorMessage || 'Error while listing PTO for sale')
      logError('Error while listing PTO for sale:', error)
    } finally {
      onCloseModalProcessing()
    }
  }

  return (
    <div className="p2p-create">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleListForSale}
        onFinishFailed={notifyFormErrors}
        scrollToFirstError={{
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        }}
      >
        {/* Hidden - used by timelines validators */}
        <Form.Item name="maxNFTSupply" hidden>
          <InputNumber />
        </Form.Item>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: 10,
          }}
        >
          <div>
            Selling Status:&nbsp;
            <Tag color={sellingStatusConfig.color}>{sellingStatusConfig.label}</Tag>
          </div>
          <div>
            {isOffSale && !listing ? (
              <Button type="primary" onClick={handleStartListing}>
                List For Sale
              </Button>
            ) : null}
            {listing ? (
              <>
                <Button style={{ marginRight: 15 }} onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  List For Sale
                </Button>
              </>
            ) : null}
          </div>
        </div>

        <fieldset>
          <legend>Selling Information</legend>
          <Row gutter={24}>
            {/* NFT Price - same specs as v1 basic info */}
            <Col span={12}>
              <Form.Item
                label={
                  <>
                    NFT Price
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your PTO NFT price, please enter your chosen price as number
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="pricePerNFT"
                rules={[
                  { required: true, message: 'Please input the nft price!' },
                  {
                    type: 'number',
                    min: 0.0000001,
                    message: 'NFT price must be greater than or equal to 0.0000001!',
                  },
                ]}
              >
                <InputNumber
                  style={{ color: 'rgba(0, 0, 0, 0.85)', width: '100%' }}
                  min={0.0000001}
                  placeholder="Enter the nft price!"
                  disabled={!editable}
                  addonBefore={'$'}
                  addonAfter={'USDC'}
                />
              </Form.Item>
            </Col>

            {/* Transaction Fee */}
            <Col span={12}>
              <Form.Item
                label={
                  <>
                    Transaction Fee
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your PTO transaction fee, please enter the correct transaction
                            fee and check its information
                          </p>
                        </div>
                      }
                      placement="right"
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="transactionFee"
                rules={[
                  {
                    required: true,
                    message: 'Please input the transaction fee!',
                  },
                ]}
              >
                <InputNumber
                  style={{ color: 'rgba(0, 0, 0, 0.85)', width: '100%' }}
                  min={0}
                  max={100}
                  placeholder="Enter the transaction fee"
                  disabled={!editable}
                  addonAfter={'%'}
                />
              </Form.Item>
            </Col>
          </Row>
        </fieldset>

        <Timelines form={form} editable={editable} />

        <fieldset>
          <legend>Whitelist</legend>
          <ButtonUploadWhitelist id={code} edit={whitelistEdit} />
        </fieldset>
      </Form>
    </div>
  )
}

export default SellingDetails
